// SCR-STAFF-906 (بخش تقویم — US-PLT-072): روزهای خاص مرکز یا شعبه (تعطیل، باز در تعطیل رسمی، ساعت ویژه) که بر تعطیلات رسمی
// (HOLIDAYS_IR) مقدم‌اند؛ پیش از ثبت اثر روی نوبت‌های موجود نمایش داده می‌شود و پس از ثبت تعداد متأثر (AC-PLT-072.1) با پیام
// جابه‌جایی گروهی در SCH (رویداد platform.calendar.changed.v1).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type HolidayRequest,
  type TenantDayKind,
  type TenantHolidayView,
  type WorkloadItem,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  SelectRHF,
  TextFieldRHF,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { ImpactList } from './BranchOperations';
import { GeneralSettingsPage } from './GeneralSettingsPage';
import { pltKeys, toCount } from './common';

const KINDS: readonly TenantDayKind[] = ['Closed', 'Open', 'CustomHours'];
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

interface HolidayForm {
  from: string | null;
  to: string | null;
  kind: TenantDayKind;
  openFrom: string;
  openTo: string;
  branchId: string;
  title: string;
}

function toRequest(form: HolidayForm): HolidayRequest {
  const custom = form.kind === 'CustomHours';
  return {
    branchId: form.branchId || null,
    from: form.from,
    to: form.to ?? form.from,
    kind: form.kind,
    // TimeOnly سرور (HH:mm:ss).
    openFrom: custom ? `${form.openFrom}:00` : null,
    openTo: custom ? `${form.openTo}:00` : null,
    title: form.title.trim(),
    rowVersion: null,
  };
}

function affectedTotal(items: readonly WorkloadItem[]): number {
  return items.reduce((sum, item) => sum + toCount(item.count), 0);
}

function HolidayDialog({
  branches,
  onClose,
  onCreated,
}: {
  branches: readonly { id: string; name: string }[];
  onClose: () => void;
  onCreated: (affected: readonly WorkloadItem[]) => void;
}) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const { control, handleSubmit, formState, setError, getValues } = useForm<HolidayForm>({
    defaultValues: {
      from: null,
      to: null,
      kind: 'Closed',
      openFrom: '',
      openTo: '',
      branchId: '',
      title: '',
    },
  });
  const kind = useWatch({ control, name: 'kind' });
  const [impact, setImpact] = useState<readonly WorkloadItem[] | null>(null);

  const validate = (form: HolidayForm): boolean => {
    let ok = true;
    if (!form.from) {
      setError('from', { message: 'common:validation.required' });
      ok = false;
    }
    if (form.from && form.to && form.to < form.from) {
      setError('to', { message: 'plt:holidays.range_invalid' });
      ok = false;
    }
    if (!form.title.trim()) {
      setError('title', { message: 'common:validation.required' });
      ok = false;
    }
    if (form.kind === 'CustomHours') {
      if (!TIME.test(form.openFrom)) {
        setError('openFrom', { message: 'plt:holidays.time_invalid' });
        ok = false;
      }
      if (!TIME.test(form.openTo) || form.openTo <= form.openFrom) {
        setError('openTo', { message: 'plt:holidays.time_invalid' });
        ok = false;
      }
    }
    return ok;
  };

  const preview = useMutation({
    mutationFn: (form: HolidayForm) =>
      unwrap(tenantApi.POST('/api/v1/platform/calendar/holidays:preview', { body: toRequest(form) })),
    onSuccess: (result) => setImpact(result.affected),
  });
  const create = useMutation({
    mutationFn: (form: HolidayForm) =>
      unwrap(tenantApi.POST('/api/v1/platform/calendar/holidays', { body: toRequest(form) })),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: pltKeys.holidays(session?.tenantId) });
      onCreated(result.affected);
      onClose();
    },
  });

  return (
    <FormDialog
      open
      titleKey="plt:holidays.add_title"
      screenId="SCR-STAFF-906"
      onSubmit={() => void handleSubmit((form) => validate(form) && create.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={create.isPending}
      submitError={create.error ?? preview.error ?? undefined}
    >
      <TextFieldRHF
        control={control}
        name="title"
        labelKey="plt:holidays.day_title"
        fullWidth
        required
        slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'holiday-title' } }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <JalaliDatePickerRHF control={control} name="from" labelKey="plt:holidays.from" required />
        <JalaliDatePickerRHF
          control={control}
          name="to"
          labelKey="plt:holidays.to"
          helperKey="plt:holidays.to_help"
        />
      </Stack>
      <SelectRHF
        control={control}
        name="kind"
        labelKey="plt:holidays.kind"
        fullWidth
        options={KINDS.map((value) => ({ value, label: t(`plt:holidays.kinds.${value}`) }))}
      />
      {kind === 'CustomHours' && (
        <Stack direction="row" spacing={2}>
          <TextFieldRHF
            control={control}
            name="openFrom"
            labelKey="plt:holidays.open_from"
            helperKey="plt:holidays.time_help"
            slotProps={{ htmlInput: { dir: 'ltr', maxLength: 5, placeholder: '09:00' } }}
          />
          <TextFieldRHF
            control={control}
            name="openTo"
            labelKey="plt:holidays.open_to"
            slotProps={{ htmlInput: { dir: 'ltr', maxLength: 5, placeholder: '13:00' } }}
          />
        </Stack>
      )}
      <SelectRHF
        control={control}
        name="branchId"
        labelKey="plt:holidays.branch"
        fullWidth
        options={[
          { value: '', label: t('plt:holidays.all_branches') },
          ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
        ]}
      />
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="outlined"
          onClick={() => {
            const form = getValues();
            if (validate(form)) {
              preview.mutate(form);
            }
          }}
          disabled={preview.isPending}
          data-testid="holiday-preview"
        >
          {t('plt:holidays.preview')}
        </Button>
        {impact !== null && (
          <Typography data-testid="holiday-impact" role="status">
            {affectedTotal(impact) > 0
              ? t('plt:holidays.impact', { value: faDigits(affectedTotal(impact)) })
              : t('plt:holidays.no_impact')}
          </Typography>
        )}
      </Stack>
    </FormDialog>
  );
}

export function HolidaysSection() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('plt.settings.manage');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<TenantHolidayView | null>(null);
  const [created, setCreated] = useState<readonly WorkloadItem[] | null>(null);

  const holidays = useQuery({
    queryKey: pltKeys.holidays(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/calendar/holidays')),
  });
  const branches = useQuery({
    queryKey: pltKeys.branches(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/branches')),
  });
  const branchItems = (branches.data?.items ?? []).map((branch) => ({ id: branch.id, name: branch.name }));
  const branchName = (id: string | null | undefined) =>
    id ? (branchItems.find((branch) => branch.id === id)?.name ?? id) : t('plt:holidays.all_branches');

  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.DELETE('/api/v1/platform/calendar/holidays/{id}', { params: { path: { id } } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pltKeys.holidays(session?.tenantId) });
      setRemoving(null);
    },
  });

  const columns: DataTableColumn<TenantHolidayView>[] = [
    { id: 'title', headerKey: 'plt:holidays.day_title', cell: (row) => row.title },
    {
      id: 'range',
      headerKey: 'plt:holidays.range',
      cell: (row) =>
        row.from === row.to ? (
          <JalaliDate value={row.from} />
        ) : (
          <>
            <JalaliDate value={row.from} /> – <JalaliDate value={row.to} />
          </>
        ),
    },
    {
      id: 'kind',
      headerKey: 'plt:holidays.kind',
      cell: (row) =>
        row.kind === 'CustomHours' ? (
          <>
            {t('plt:holidays.kinds.CustomHours')}{' '}
            <bdi dir="ltr">
              {faDigits(row.openFrom?.slice(0, 5) ?? '')}–{faDigits(row.openTo?.slice(0, 5) ?? '')}
            </bdi>
          </>
        ) : (
          t(`plt:holidays.kinds.${row.kind}`)
        ),
    },
    { id: 'branch', headerKey: 'plt:holidays.branch', cell: (row) => branchName(row.branchId) },
    {
      id: 'actions',
      headerKey: 'plt:branches.columns.actions',
      cell: (row) => (
        <Tooltip title={t('plt:holidays.remove')}>
          <span>
            <IconButton
              aria-label={t('plt:holidays.remove')}
              onClick={() => setRemoving(row)}
              disabled={!write.canWrite}
              data-testid={`holiday-remove-${row.id}`}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mt: 3 }}
      component="section"
      aria-labelledby="holidays-title"
      data-testid="holidays-section"
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" id="holidays-title">
          {t('plt:holidays.title')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAdding(true)}
          disabled={!write.canWrite}
          data-testid="holiday-add"
        >
          {t('plt:holidays.add')}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('plt:holidays.subtitle')}{' '}
        <Link component={RouterLink} to="/settings/holidays" data-testid="holiday-manage-link">
          {t('plt:holidays.manage_link')}
        </Link>
      </Typography>
      {created !== null && (
        <Alert
          severity={affectedTotal(created) > 0 ? 'warning' : 'success'}
          sx={{ mb: 2 }}
          data-testid="holiday-created"
          role="status"
        >
          {affectedTotal(created) > 0
            ? t('plt:holidays.created_with_impact', { value: faDigits(affectedTotal(created)) })
            : t('plt:holidays.created')}
          {affectedTotal(created) > 0 && <ImpactList items={created} />}
        </Alert>
      )}
      <DataTable
        captionKey="plt:holidays.caption"
        columns={columns}
        rows={holidays.data?.items}
        getRowId={(row) => row.id}
        isLoading={holidays.isPending}
        error={holidays.error}
        onRetry={() => void holidays.refetch()}
        emptyTitleKey="plt:holidays.empty"
      />
      {adding && (
        <HolidayDialog branches={branchItems} onClose={() => setAdding(false)} onCreated={setCreated} />
      )}
      <ConfirmDialog
        open={removing !== null}
        titleKey="plt:holidays.remove_title"
        messageKey="plt:holidays.remove_message"
        messageValues={{ title: removing?.title ?? '' }}
        destructive
        busy={remove.isPending}
        error={remove.error}
        onConfirm={() => removing && remove.mutate(removing.id)}
        onCancel={() => setRemoving(null)}
      />
    </Paper>
  );
}

/** SCR-STAFF-906: تنظیمات عمومی (AP-PLT-02) و تقویم مرکز (AP-PLT-05) در یک صفحه. */
export function GeneralAndCalendarPage() {
  return (
    <>
      <GeneralSettingsPage />
      <HolidaysSection />
    </>
  );
}
